import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Box } from "@mui/material";
import GridTable from "../../components/GridTable";
import SearchIcon from "@mui/icons-material/Search";
import Anchor from "@mui/icons-material/Anchor";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import Dialog from "../../components/Dialog";
import Loader from "../../components/Loader";
import Button from "../../components/Button";
import Dropdown from "../../components/Dropdown";
import ActionMenu from "../../components/ActionMenu";
import { successMessage, errorMessage } from "../../components/Toaster";
import {
  getShippingLinesByCompanyId,
  createShippingLine,
  updateShippingLine,
  deleteShippingLine,
} from "./api";
import { getYardsByCompanyId } from "../Home/api";
import { getErrorMessage } from "../../utils/error";
import { VISO_SUPER_ADMIN } from "../../constants"; 

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ChipEmailInput = ({ label, chips, onAdd, onRemove, error, helperText }) => {
  const [inputValue, setInputValue] = useState("");
  const [inputError, setInputError] = useState("");

  const handleAdd = () => {
    const email = inputValue.trim();
    if (!email) return;
    if (!EMAIL_REGEX.test(email)) {
      setInputError("Enter a valid email (e.g. name@example.com)");
      return;
    }
    if (chips.includes(email)) {
      setInputError("Email already added");
      return;
    }
    onAdd(email);
    setInputValue("");
    setInputError("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      handleAdd();
    }
  };

  return (
    <Box>
      {chips.length > 0 && (
        <Box>
          {chips.map((email, i) => (
            <Chip
              key={i}
              label={email}
              onDelete={() => onRemove(email)}
              sx={{
                backgroundColor: "#e3f2fd",
                color: "#1565c0",
                fontWeight: 500,
                fontSize: "14px",
                "& .MuiChip-deleteIcon": { color: "#1565c0" },
              }}
            />
          ))}
        </Box>
      )}
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
        <TextField
          fullWidth
          label={`${label} *`}
          size="small"
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            setInputError("");
          }}
          onKeyDown={handleKeyDown}
          error={Boolean(inputError) || error}
          helperText={inputError || helperText || "Press Enter or click + to add"}
          placeholder="name@example.com"
        />
        <Tooltip title={`Add ${label}`}>
          <IconButton
            onClick={handleAdd}
            color="primary"
            sx={{
              backgroundColor: "#1976d2",
              color: "white",
              width: 38,
              height: 38,
              borderRadius: "8px",
              mt: "8px",
              flexShrink: 0,
              "&:hover": { backgroundColor: "#1565c0" },
            }}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

const EmailCell = ({ emails }) => {
  if (!Array.isArray(emails) || emails.length === 0) return <span>-</span>;
  return (
    <Tooltip
      arrow
      placement="top"
      title={
        <Box sx={{ p: "4px 2px" }}>
          {emails.map((email, i) => (
            <div key={i} style={{ fontSize: "12px", padding: "2px 0" }}>
              {email}
            </div>
          ))}
        </Box>
      }
    >
      <span
        style={{
          display: "block",
          maxWidth: "300px",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          cursor: "pointer",
        }}
      >
        {emails.join(", ")}
      </span>
    </Tooltip>
  );
};

const ShippingLine = () => {
  const role = localStorage.getItem("role_name");
  const isVisoAdmin = role === VISO_SUPER_ADMIN;
  const localCompanyId = localStorage.getItem("company_id");
  const filters = useSelector((state) => state.filters);
  const companyId = isVisoAdmin
    ? filters.selectedCompanyId || localCompanyId
    : localCompanyId;

  const [shippingLines, setShippingLines] = useState([]);
  const [yardOptions, setYardOptions] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [editShippingLine, setEditShippingLine] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [ccChips, setCcChips] = useState([]);
  const [toChips, setToChips] = useState([]);
  const [ccError, setCcError] = useState("");
  const [toError, setToError] = useState("");

  const [loading, setLoading] = useState({
    create: false,
    fetch: false,
    delete: false,
    update: false,
  });

  const validationSchema = Yup.object().shape({
    shippingLineName: Yup.string().required("Shipping Line is required"),
    yardId: Yup.string().required("Yard is required"),
  });

  const columns = [
    {
      field: "shipping_line_name",
      headerName: "Shipping Line",
      headerClassName: "table-header",
      flex: 1,
    },
    {
      field: "yard_name",
      headerName: "Yard",
      headerClassName: "table-header",
      flex: 1,
    },
    {
      field: "cc_emails",
      headerName: "Sender Emails",
      headerClassName: "table-header",
      flex: 1,
      renderCell: (params) => <EmailCell emails={params.row?.cc_emails} />,
    },
    {
      field: "to_emails",
      headerName: "To Emails",
      headerClassName: "table-header",
      flex: 1,
      renderCell: (params) => <EmailCell emails={params.row?.to_emails} />,
    },
    {
      field: "actions",
      headerName: "Actions",
      headerClassName: "table-header",
      renderCell: (params) => (
        <ActionMenu
          params={params}
          actions={[
            {
              icon: <EditIcon color="primary" />,
              tooltip: "Edit",
              onClick: handleEdit,
            },
            {
              icon: <DeleteIcon color="error" />,
              tooltip: "Delete",
              onClick: handleDelete,
            },
          ]}
        />
      ),
      sortable: false,
      filterable: false,
      flex: 1,
    },
  ];

  // ✅ Re-fetch when companyId changes (viso admin switches company)
  useEffect(() => {
    if (!companyId) return;
    const fetchShippingLines = async () => {
      setLoading((prev) => ({ ...prev, fetch: true }));
      try {
        const res = await getShippingLinesByCompanyId(companyId);
        if (res.status && res.data) setShippingLines(res.data);
        else errorMessage(res.message || "Failed to fetch shipping lines.");
      } catch (error) {
        errorMessage(getErrorMessage(error) || "Error fetching shipping lines.");
      } finally {
        setLoading((prev) => ({ ...prev, fetch: false }));
      }
    };
    fetchShippingLines();
  }, [companyId]); // ✅ re-runs when company changes

  const handleEdit = (params) => {
    setEditShippingLine(params.row);
    openDialog(params.row);
  };

  const handleDelete = (params) => {
    setItemToDelete(params);
    setDeleteOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteOpen(false);
    setItemToDelete(null);
  };

  const openDialog = async (row) => {
    if (row) {
      setCcChips(Array.isArray(row.cc_emails) ? row.cc_emails : []);
      setToChips(Array.isArray(row.to_emails) ? row.to_emails : []);
    } else {
      setCcChips([]);
      setToChips([]);
    }
    setCcError("");
    setToError("");
    setDialogOpen(true);

    setLoading((prev) => ({ ...prev, fetch: true }));
    try {
      const res = await getYardsByCompanyId(companyId); // ✅ uses correct companyId
      if (res.status && Array.isArray(res.data)) {
        setYardOptions(
          res.data.map((y) => ({ label: y.yard_name, value: y.yard_id }))
        );
      } else {
        errorMessage(res.message || "Failed to fetch yards.");
      }
    } catch (error) {
      errorMessage(getErrorMessage(error) || "Error fetching yards.");
    } finally {
      setLoading((prev) => ({ ...prev, fetch: false }));
    }
  };

  const closeDialog = (resetForm) => {
    resetForm();
    setDialogOpen(false);
    setEditShippingLine(null);
    setCcChips([]);
    setToChips([]);
    setCcError("");
    setToError("");
  };

  const validateChips = () => {
    let valid = true;
    if (ccChips.length === 0) {
      setCcError("At least one sender email is required");
      valid = false;
    } else setCcError("");
    if (toChips.length === 0) {
      setToError("At least one to email is required");
      valid = false;
    } else setToError("");
    return valid;
  };

  const confirmDialog = async (values, { resetForm }) => {
    if (!validateChips()) return;
    let success = false;
    if (editShippingLine) success = await handleUpdate(values);
    else success = await handleCreate(values);
    if (success) closeDialog(resetForm);
  };

  const handleCreate = async (values) => {
    setLoading((prev) => ({ ...prev, create: true }));
    try {
      const payload = {
        shipping_line_name: values.shippingLineName.trim(),
        yard_id: values.yardId,
        cc_emails: ccChips,
        to_emails: toChips,
      };
      const response = await createShippingLine(payload);
      if (response?.status) {
        const selectedYard = yardOptions.find((y) => y.value === values.yardId);
        setShippingLines((prev) => [
          ...prev,
          {
            ...payload,
            yard_name: selectedYard?.label || "",
            shipping_line_id: response?.data?.shipping_line_id,
          },
        ]);
        successMessage(response.message || "Shipping line created successfully!");
        return true;
      } else {
        errorMessage(response?.message || "Failed to create shipping line.");
        return false;
      }
    } catch (error) {
      errorMessage(getErrorMessage(error) || "An unexpected error occurred.");
      return false;
    } finally {
      setLoading((prev) => ({ ...prev, create: false }));
    }
  };

  const handleUpdate = async (values) => {
    setLoading((prev) => ({ ...prev, update: true }));
    try {
      const payload = {
        shipping_line_id: editShippingLine?.shipping_line_id,
        shipping_line_name: values.shippingLineName.trim(),
        yard_id: values.yardId,
        cc_emails: ccChips,
        to_emails: toChips,
      };
      const response = await updateShippingLine(payload);
      if (response?.status) {
        setShippingLines((prev) =>
          prev.map((sl) =>
            sl.shipping_line_id === payload.shipping_line_id
              ? { ...sl, ...payload }
              : sl
          )
        );
        successMessage(response.message || "Shipping line updated successfully!");
        return true;
      } else {
        errorMessage(response?.message || "Failed to update shipping line.");
        return false;
      }
    } catch (error) {
      errorMessage(getErrorMessage(error) || "An unexpected error occurred.");
      return false;
    } finally {
      setLoading((prev) => ({ ...prev, update: false }));
    }
  };

  const handleDeleteConfirm = async () => {
    setLoading((prev) => ({ ...prev, delete: true }));
    if (itemToDelete) {
      try {
        const response = await deleteShippingLine(itemToDelete.row.shipping_line_id);
        if (response.status) {
          setShippingLines((prev) =>
            prev.filter(
              (sl) => sl.shipping_line_id !== itemToDelete.row.shipping_line_id
            )
          );
          successMessage(response.message || "Shipping line deleted successfully!");
        } else {
          errorMessage(response.message || "Failed to delete shipping line.");
        }
      } catch (error) {
        errorMessage(getErrorMessage(error) || "An unexpected error occurred.");
      } finally {
        setLoading((prev) => ({ ...prev, delete: false }));
        setDeleteOpen(false);
      }
    }
  };

  return (
    <>
      {loading.fetch ? (
        <Loader color="primary" size={40} />
      ) : (
        <Box>
          <main className="m-a-50">
            <div className="d-flex justify-end">
              <div className="d-flex justify-between w-100">
                <TextField
                  className="w-25"
                  label="Search"
                  variant="outlined"
                  value={searchQuery}
                  size="small"
                  startIcon={<SearchIcon />}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <Button
                  label="Add"
                  variant="contained"
                  color="primary"
                  onClick={() => openDialog()}
                />
              </div>
            </div>

            <div
              style={{ height: "100%", width: "100%" }}
              className="ag-theme-alpine m-t-20"
            >
              <GridTable
                rows={shippingLines}
                columns={columns}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                getRowId={(row) => row.shipping_line_id}
                density="standard"
              />
            </div>

            {/* ── Add / Edit Dialog ── */}
            <Formik
              initialValues={{
                shippingLineName: editShippingLine?.shipping_line_name ?? "",
                yardId: editShippingLine?.yard_id ?? "",
              }}
              validationSchema={validationSchema}
              onSubmit={confirmDialog}
              enableReinitialize
            >
              {({
                values,
                errors,
                touched,
                handleChange,
                handleSubmit,
                resetForm,
                setFieldValue,
              }) => (
                <Dialog
                  open={isDialogOpen}
                  onClose={() => closeDialog(resetForm)}
                  onConfirm={handleSubmit}
                  title={
                    !loading.update && !loading.create
                      ? editShippingLine
                        ? "Edit Shipping Line"
                        : "Add Shipping Line"
                      : ""
                  }
                  confirmButtonText={
                    !loading.update && !loading.create
                      ? editShippingLine
                        ? "Update"
                        : "Add"
                      : ""
                  }
                  cancelButtonText={
                    !loading.update && !loading.create ? "Cancel" : ""
                  }
                  icon={<Anchor />}
                  message={
                    <Box padding={4}>
                      {loading.create || loading.update ? (
                        <Loader color="primary" size={40} />
                      ) : (
                        <Form>
                          <Grid container spacing={3}>
                            <Grid size={6}>
                              <TextField
                                fullWidth
                                id="shippingLineName"
                                name="shippingLineName"
                                label="Shipping Line *"
                                value={values.shippingLineName}
                                onChange={handleChange}
                                error={
                                  touched.shippingLineName &&
                                  Boolean(errors.shippingLineName)
                                }
                                helperText={
                                  touched.shippingLineName &&
                                  errors.shippingLineName
                                }
                              />
                            </Grid>

                            <Grid size={6}>
                              <Dropdown
                                options={yardOptions.map((y) => ({
                                  id: y.value,
                                  name: y.label,
                                }))}
                                value={values.yardId ?? ""}
                                onChange={(id) => setFieldValue("yardId", id)}
                                label="Yard"
                                required
                                error={Boolean(touched.yardId && errors.yardId)}
                                helperText={
                                  touched.yardId && errors.yardId
                                    ? String(errors.yardId)
                                    : undefined
                                }
                              />
                            </Grid>

                            <Grid size={12}>
                              <ChipEmailInput
                                label="Sender Email"
                                chips={ccChips}
                                onAdd={(email) =>
                                  setCcChips((prev) => [...prev, email])
                                }
                                onRemove={(email) =>
                                  setCcChips((prev) =>
                                    prev.filter((e) => e !== email)
                                  )
                                }
                                error={Boolean(ccError)}
                                helperText={ccError}
                              />
                            </Grid>

                            <Grid size={12}>
                              <ChipEmailInput
                                label="To Email"
                                chips={toChips}
                                onAdd={(email) =>
                                  setToChips((prev) => [...prev, email])
                                }
                                onRemove={(email) =>
                                  setToChips((prev) =>
                                    prev.filter((e) => e !== email)
                                  )
                                }
                                error={Boolean(toError)}
                                helperText={toError}
                              />
                            </Grid>
                          </Grid>
                        </Form>
                      )}
                    </Box>
                  }
                />
              )}
            </Formik>

            <Dialog
              open={deleteOpen}
              onClose={handleDeleteCancel}
              onConfirm={handleDeleteConfirm}
              title={!loading.delete ? "Delete Shipping Line" : ""}
              confirmButtonText={!loading.delete ? "Confirm" : ""}
              cancelButtonText={!loading.delete ? "Cancel" : ""}
              icon={<DeleteIcon />}
              message={
                <Box padding={2}>
                  {loading.delete ? (
                    <Loader color="primary" size={40} />
                  ) : (
                    "Are you sure you want to delete this shipping line?"
                  )}
                </Box>
              }
            />
          </main>
        </Box>
      )}
    </>
  );
};

export default ShippingLine;